import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { verificationEmailUrl } from "../firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc, collection, getDocs } from "firebase/firestore";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const AddUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    patientId: "",
    doctorId: "",
    password: "",
    confirmPassword: "",
    skipVerification: false,
  });
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch doctors on component mount
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const doctorsSnapshot = await getDocs(collection(db, "doctors"));
        const doctorsList = doctorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDoctors(doctorsList);
      } catch (error) {
        console.error("Error fetching doctors:", error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear errors when user starts typing
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.password) {
      setError("Email, First Name, Last Name, and Password are required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Invalid email address");
      return false;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Validate phone number format if provided (optional field)
    if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        setError("Invalid phone number format");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Check if user already exists in Firestore
      const userRef = doc(db, "users", formData.email);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const overwrite = window.confirm(
          "User already exists in Firestore. Do you want to overwrite?"
        );
        if (!overwrite) {
          setLoading(false);
          return;
        }
      }

      // Step 2: Create Firebase Auth account
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        console.log("✅ Firebase Auth account created:", userCredential.user.email);
      } catch (authError) {
        if (authError.code === "auth/email-already-in-use") {
          console.log("⚠️ Firebase Auth account already exists, continuing...");
        } else {
          throw authError;
        }
      }

      // Step 3: Create Firestore user document
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();

      // Combine firstName and lastName for username (backward compatibility)
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();

      // Get doctor info if selected
      let doctorInfo = null;
      if (formData.doctorId) {
        const selectedDoctor = doctors.find((doc) => doc.id === formData.doctorId);
        if (selectedDoctor) {
          doctorInfo = {
            doctorId: selectedDoctor.id,
            doctorName: selectedDoctor.name,
            doctorEmail: selectedDoctor.email,
            doctorSpecialty: selectedDoctor.specialty || null,
          };
        }
      }

      const userData = {
        username: fullName, // Keep for backward compatibility
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null, // Phone number (optional)
        password: formData.password, // Store password for custom auth
        patientId: formData.patientId || null, // Patient ID (optional)
        doctorId: doctorInfo?.doctorId || null, // Doctor ID (optional)
        doctorName: doctorInfo?.doctorName || null, // Doctor name for easy reference
        doctorEmail: doctorInfo?.doctorEmail || null, // Doctor email for easy reference
        doctorSpecialty: doctorInfo?.doctorSpecialty || null, // Doctor specialty
        verificationCode,
        isVerified: formData.skipVerification,
        createdAt: new Date().toISOString(),
        profileImage: null,
        notificationsEnabled: true,
        fhirPatientId: formData.patientId || null, // Use patientId if provided
      };

      await setDoc(userRef, userData);
      console.log("✅ Firestore user document created");

      // Step 4: Send verification email (if not skipped)
      if (!formData.skipVerification) {
        if (!verificationEmailUrl) {
          console.warn(
            "⚠️ REACT_APP_VERIFICATION_EMAIL_URL is not set; skipping verification email."
          );
        } else {
          try {
            const axios = (await import("axios")).default;
            await axios.post(
              verificationEmailUrl,
              {
                email: formData.email,
                code: verificationCode,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            console.log("✅ Verification email sent");
          } catch (emailError) {
            console.warn("⚠️ Verification email failed:", emailError.message);
          }
        }
      }

      const successMessage = `User created successfully! ${formData.skipVerification ? "User is verified." : "Verification code: " + verificationCode}${doctorInfo ? ` Assigned to: ${doctorInfo.doctorName}` : ""}`;
      setSuccess(successMessage);

      // Reset form
      setFormData({
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        patientId: "",
        doctorId: "",
        password: "",
        confirmPassword: "",
        skipVerification: false,
      });
    } catch (error) {
      console.error("Error creating user:", error);
      let errorMessage = "Failed to create user. Please try again.";

      switch (error.code) {
        case "auth/operation-not-allowed":
        case "auth/configuration-not-found":
          errorMessage =
            "Email/Password authentication is not enabled. Please enable it in Firebase Console. Go to Authentication > Sign-in method > Email/Password > Enable > Save.";
          console.error("❌ Firebase Auth Configuration Error:");
          console.error("1. Go to: https://console.firebase.google.com/");
          console.error(
            "2. Select project:",
            auth?.app?.options?.projectId || "(your Firebase project)"
          );
          console.error("3. Navigate to: Authentication > Sign-in method");
          console.error("4. Click on 'Email/Password'");
          console.error("5. Toggle 'Enable' to ON");
          console.error("6. Click 'Save'");
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address.";
          break;
        case "auth/weak-password":
          errorMessage = "Password is too weak. Please choose a stronger password.";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection.";
          break;
        default:
          errorMessage = error.message || "Failed to create user. Please try again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="appointments-container">
      {/* Sidebar */}
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/add-user" className="active">
            <img src={userIcon} alt="Add User" className="menu-icon" />
            Add User
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      {/* Main Content */}
      <div className="appointments-page">
        <h1 className="dashboard-title">Add New User</h1>

        {error && (
          <div
            className="error-message"
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="success-message"
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            maxWidth: "600px",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Email Address *</label>
              <input
                type="email"
                className="login-input"
                name="email"
                placeholder="user@example.com"
                required
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">First Name *</label>
              <input
                type="text"
                className="login-input"
                name="firstName"
                placeholder="John"
                required
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Last Name *</label>
              <input
                type="text"
                className="login-input"
                name="lastName"
                placeholder="Doe"
                required
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Phone Number</label>
              <input
                type="tel"
                className="login-input"
                name="phoneNumber"
                placeholder="+1 (555) 123-4567"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Patient ID</label>
              <input
                type="text"
                className="login-input"
                name="patientId"
                placeholder="Enter patient ID (optional)"
                value={formData.patientId}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Assigned Doctor</label>
              <select
                className="login-input"
                name="doctorId"
                value={formData.doctorId}
                onChange={handleInputChange}
                disabled={loading || loadingDoctors}
                style={{
                  padding: "12px",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  width: "100%",
                  backgroundColor: loading || loadingDoctors ? "#f5f5f5" : "#fff",
                }}
              >
                <option value="">Select a doctor (optional)</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name} {doctor.specialty ? `- ${doctor.specialty}` : ""}
                  </option>
                ))}
              </select>
              {loadingDoctors && (
                <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                  Loading doctors...
                </p>
              )}
              {!loadingDoctors && doctors.length === 0 && (
                <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                  No doctors available. Please add doctors first.
                </p>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Password *</label>
              <input
                type="password"
                className="login-input"
                name="password"
                placeholder="Minimum 6 characters"
                required
                value={formData.password}
                onChange={handleInputChange}
                disabled={loading}
                minLength={6}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Confirm Password *</label>
              <input
                type="password"
                className="login-input"
                name="confirmPassword"
                placeholder="Confirm password"
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={loading}
                minLength={6}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  name="skipVerification"
                  checked={formData.skipVerification}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span>Skip email verification (user will be verified immediately)</span>
              </label>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
              style={{
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "not-allowed" : "pointer",
                width: "100%",
              }}
            >
              {loading ? "Creating User..." : "Create User"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;

